import { ContextStore, ExperimentStore } from 'bernie-plugin-mobx';

import { ILocalization } from 'bernie-l10n';
import { updateSearch } from 'bernie-core';

import { SearchFormInputs } from '@shared-ui/search-tools-lodging';

import { evaluateAndLogExperiment } from 'stores/utils';
import { SearchResultsStore } from 'src/common/stores/search-results-store';

import { HOUSE_RULES_GROUP } from 'src/common/constants/filters';
import { MarketingChannels, MarketingChannelsTypeCodes } from 'constants/marketing-channels';

import { getAdultsAndChildrenFromRooms } from 'src/common/utils/room-utils';

import { History, Location } from 'history';
import { PropertyDateRangeInput, PropertyFilterAmenity, SelectedValueInput } from 'src/common/__generated__/api';

interface UpdatedSearchParams {
  destination: string;
  regionId: string;
  startDate: string;
  endDate: string;
  adults: string;
  children: string;
  amenities?: string;
  // eslint-disable-next-line camelcase
  total_price?: string;
  pwaDialog?: string;
  selected?: string;
  // eslint-disable-next-line camelcase
  house_rules_group?: HOUSE_RULES_GROUP[];
  // eslint-disable-next-line camelcase
  neighborhood_group?: string;
}

export const updateResultsWithNewCriteria = (
  { propertySearchCriteriaInput: criteria }: SearchFormInputs,
  location: Location,
  history: History,
  searchResults: SearchResultsStore,
  experiment: ExperimentStore,
  l10n: ILocalization
) => {
  searchResults.clearDefaultRequestFields();
  searchResults.shouldFetch = true;

  const { primary, secondary } = criteria || {};
  const { regionName: destination, regionId, coordinates, pinnedPropertyId } = primary?.destination || {};
  const dateStrings = primary?.dateRange ? getDateStringsFromDateRangeInput(primary.dateRange, l10n) : undefined;
  const startDate = dateStrings?.startDate;
  const endDate = dateStrings?.endDate;
  const adultsAndChidlren = primary?.rooms ? getAdultsAndChildrenFromRooms(primary.rooms) : undefined;
  const adults = adultsAndChidlren?.adults;
  const children = adultsAndChidlren?.children;
  const latLong = coordinates ? [coordinates.latitude, coordinates.longitude].join(',') : '';
  const params = new URLSearchParams(location.search);
  const searchParamStartDate = params.get('startDate');
  const searchParamEndDate = params.get('endDate');
  const searchParamRegionId = params.get('regionId');

  const newParams: any = {
    destination,
    latLong,
    regionId,
    startDate,
    endDate,
    adults,
    children,
    pwaDialog: null,
    selected: pinnedPropertyId,
    mapBounds: '',
  };

  if (pinnedPropertyId) {
    newParams.regionId = null;
  }

  if (startDate !== searchParamStartDate || endDate !== searchParamEndDate) {
    // setting the total_price property of the newParams object explicitly to undefined so that updateSearch()
    // removes the total_price url parameter. nightly_price should remain unaffected by date changes.

    // eslint-disable-next-line camelcase
    (newParams as unknown as UpdatedSearchParams).total_price = undefined;
  }

  if (regionId !== searchParamRegionId) {
    // eslint-disable-next-line camelcase
    (newParams as unknown as UpdatedSearchParams).neighborhood_group = undefined;
  }

  if (experiment?.exposures?.Vrbex_generic?.bucket === 1) {
    (newParams as unknown as UpdatedSearchParams).amenities = getAmenities(searchResults, secondary?.selections);
    // eslint-disable-next-line camelcase
    (newParams as unknown as UpdatedSearchParams).house_rules_group = getHouseRules(searchResults, secondary?.selections);
  }

  updateSearch({ location, history, newParams, replace: false });
};

export const getHouseRules = (searchResults: SearchResultsStore, selections?: SelectedValueInput[] | null): HOUSE_RULES_GROUP[] => {
  let rules = searchResults.searchCriteria.house_rules_group;
  if (selections) {
    rules = rules.filter(rule => rule !== HOUSE_RULES_GROUP.PETS_ALLOWED);
    const selectionsHasPets = selections.some(sel => sel.value === HOUSE_RULES_GROUP.PETS_ALLOWED);
    if (selectionsHasPets) {
      rules.push(HOUSE_RULES_GROUP.PETS_ALLOWED);
    }
  }

  return rules;
};

export const getAmenities = (searchResults: SearchResultsStore, selections?: SelectedValueInput[] | null): string => {
  let amenities = searchResults.searchCriteria.amenities;

  if (selections) {
    amenities = amenities.filter(amenity => amenity !== PropertyFilterAmenity.PETS);
    const selectionsHasPets = selections.some(sel => sel.value === HOUSE_RULES_GROUP.PETS_ALLOWED);
    if (selectionsHasPets) {
      amenities.push(PropertyFilterAmenity.PETS);
    }
  }

  return amenities.filter(Boolean).join();
};

export const getDateStringsFromDateRangeInput = (dateRange: PropertyDateRangeInput, l10n: ILocalization) => {
  if (dateRange === null) {
    return { startDate: undefined, endDate: undefined };
  }

  const { checkInDate, checkOutDate } = dateRange;
  const startDateObj = new Date(checkInDate.year, checkInDate.month - 1, checkInDate.day);
  const endDateObj = new Date(checkOutDate.year, checkOutDate.month - 1, checkOutDate.day);

  return {
    startDate: l10n.formatDate(startDateObj, { raw: 'yyyy-MM-dd' }),
    endDate: l10n.formatDate(endDateObj, { raw: 'yyyy-MM-dd' }),
  };
};

export const isVrbexExperiment = (experiment: ExperimentStore) => experiment?.exposures?.Vrbex_generic?.bucket === 1;

export const isSRPCalendarAutoOpenExperiment = (experiment: ExperimentStore, context: ContextStore) =>
  Boolean(evaluateAndLogExperiment('Landing_SRP_Calendar_opening_by_default', context, experiment));

export const isSRPAutoOpenPlaybackExperiment = (experiment: ExperimentStore, context: ContextStore) =>
  evaluateAndLogExperiment('SRP_mWeb_Auto_open_playback_component', context, experiment) === 1;

export const isMetaChannelDefaultDatesSearch = (searchQueryParams: URLSearchParams) => {
  const marketingChannelTypeCode = searchQueryParams.get('mctc');
  switch (marketingChannelTypeCode) {
    case MarketingChannelsTypeCodes.TRIVAGO:
      return Boolean(searchQueryParams.get(MarketingChannels.mdpdtl)?.includes('defdate=1'));
    case MarketingChannelsTypeCodes.TRIPADVISOR:
      return (
        Boolean(searchQueryParams.get(MarketingChannels.icmdtl)?.includes('defdate.1')) ||
        Boolean(searchQueryParams.get(MarketingChannels.icmdtl.toUpperCase())?.includes('defdate.1'))
      );
    case MarketingChannelsTypeCodes.GOOGLE_HA:
      return Boolean(searchQueryParams.get(MarketingChannels.mdpdtl.toUpperCase())?.includes('DDT'));
    default:
      return false;
  }
};

export const isSEMChannelDefaultDatesSearch = (searchQueryParams: URLSearchParams) =>
  searchQueryParams.get(MarketingChannels.semcid) !== null;
